import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  Drawer,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { grey, orange } from "@mui/material/colors";
import {
  GetSinglePlayList,
  Playlist,
  getPlayList,
  getSingleVideo,
  getVideoList,
  sendPutRequest,
  uploadVideoThumbnail,
} from "../../services/ContentServices";
import {
  AppBanner,
  ICreateBanner,
  createBanner,
} from "../../services/ContentManagementService";
import { Video } from "../content/types";
import {
  GetSubScriptionList,
  GetSubscriptionById,
  ISubscription,
} from "../../services/SubscriptionServices";
import {
  TableBodyPlayList,
  TableBodySubsciptionList,
  TableBodyVideoList,
} from "../../utils/ContentCardList";
import { useSnackbar } from "notistack";
import {
  PlaylistThumbCard,
  SubscriptionThumbCard,
  VideoThumbCard,
} from "../../components/ContentCard";

interface DrawerBanerManagementProps {
  open: boolean;
  onClose: () => void;
  position: 0 | 1 | 2;
  banner: AppBanner;
  onUpdate: () => void;
  onRemove: (banner: AppBanner) => Promise<number>;
}

const DrawerBanerManagement: React.FC<DrawerBanerManagementProps> = ({
  open,
  onClose,
  position,
  banner,
  onUpdate,
  onRemove,
}) => {
  const theme = useTheme();

  const [viewMode, setViewMode] = useState<boolean>(false);
  const [tempPreviewThumb, setTempPreviewThumb] = useState<string>("");
  const { enqueueSnackbar } = useSnackbar();
  const [newBanner, setNewBanner] = useState<{
    photoUrl: string;
    bannerType: string;
    position: 0 | 1 | 2;
  }>({
    photoUrl: "",
    bannerType: "",
    position: position, // Set a default value for position
  });
  const [dropdownValue, setDropdownValue] = useState<
    "VIDEO" | "PLAYLIST" | "SUBSCRIPTION" | ""
  >(banner?.bannerType || "");

  useEffect(() => {
    if (banner) {
      setDropdownValue(
        banner.bannerType as "VIDEO" | "PLAYLIST" | "SUBSCRIPTION"
      );
      setViewMode(true);
    } else {
      setDropdownValue("");
      setViewMode(false);
    }

    resetNewBanner();
  }, [banner, open]);

  function resetNewBanner() {
    setNewBanner({
      photoUrl: "",
      bannerType: "",
      position: position,
    });
    setTempPreviewThumb("");
  }

  const handleDropdownChange = (event: any) => {
    setDropdownValue(
      event.target.value as "VIDEO" | "PLAYLIST" | "SUBSCRIPTION"
    );
    setNewBanner((prev) => ({
      ...prev,
      bannerType: event.target.value as "VIDEO" | "PLAYLIST" | "SUBSCRIPTION",
    }));
  };

  const handleBannerUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
      if (allowedTypes.includes(file.type)) {
        const previewUrl = URL.createObjectURL(file);
        const signUrlResponse = await uploadVideoThumbnail(file.name);
        const signUrl = signUrlResponse.data?.data as string;
        const thumbAwsUrl = signUrl.split("?")[0];
        const upload = await sendPutRequest({ signUrl, file });
        if (upload.status === 200) {
          setNewBanner((prev) => ({ ...prev, photoUrl: thumbAwsUrl }));
          setTempPreviewThumb(previewUrl);
        }
      } else {
        alert("Please upload a PNG, JPEG, or JPG file.");
      }
    }
  };

  const selectDesination = async (id: string): Promise<number> => {
    const status = await handleSaveBanner(id);
    if (status == 200) {
      onUpdate();
      onClose();
      resetNewBanner();
      enqueueSnackbar("Successfully ! Banner has been Added", {
        variant: "success",
      });
      return 200;
    } else if (status == 401) {
      enqueueSnackbar("Failed!! Banner has not Added", {
        variant: "error",
      });
      return 401;
    } else {
      return 500;
    }
  };

  const handleSaveBanner = async (id: string) => {
    // Create a new banner object with the updated destination
    const bannerBody = { ...newBanner, destination: id };
    let validBanner = true;
    if (!bannerBody.photoUrl) {
      validBanner = false;
      enqueueSnackbar("Please Upload Banner Thumb First !", {
        variant: "warning",
      });
    }
    if (validBanner) {
      const result = await createBanner(bannerBody as ICreateBanner);
      if (result === 200) {
        return 200;
      } else {
        return 401;
      }
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          backgroundColor: theme.palette.background.paper,
          borderTopLeftRadius: theme.spacing(2),
          borderBottomLeftRadius: theme.spacing(2),
          width: viewMode ? "20%" : "45%",
          height: "100vh",
          boxSizing: "border-box",
        },
      }}
    >
      <Box
        component="form"
        aria-label="Video List"
        sx={{
          display: "flex",
          backgroundColor: theme.palette.background.paper,
          flexDirection: "column",
          height: "100vh",
          maxHeight: "100vh",
          boxSizing: "border-box",
        }}
      >
        <Grid container>
          <Grid item xs={viewMode ? 12 : 5}>
            <Box sx={{ padding: 2 }}>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  aspectRatio: "16 / 9",
                  backgroundColor: banner ? "transparent" : "gray",
                  border: `1.5px solid grey[400]`,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              >
                {banner || tempPreviewThumb ? (
                  <Box
                    component="img"
                    src={(banner && banner.photoUrl) || tempPreviewThumb}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Button variant="contained" component="label">
                    Upload Banner
                    <input
                      type="file"
                      hidden
                      accept=".png, .jpg, .jpeg"
                      onChange={handleBannerUpload}
                    />
                  </Button>
                )}
                {banner && (
                  <Button
                    variant="contained"
                    component="label"
                    sx={{
                      position: "absolute",
                      bottom: 8,
                      right: 8,
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      color: "white",
                    }}
                    onClick={() => {
                      onRemove(banner);
                    }}
                  >
                    Delete
                  </Button>
                )}
              </Box>

              <Box sx={{ marginTop: 2 }}>
                <Select
                  labelId="dropdown-label"
                  id="dropdown"
                  value={dropdownValue}
                  fullWidth
                  disabled={viewMode}
                  onChange={handleDropdownChange}
                >
                  <MenuItem value="" disabled>
                    <em>Options</em>
                  </MenuItem>
                  <MenuItem value="VIDEO">Video</MenuItem>
                  <MenuItem value="PLAYLIST">Playlist</MenuItem>
                  <MenuItem value="SUBSCRIPTION">Subscription</MenuItem>
                </Select>
              </Box>

              {banner?.id && (
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "16 / 9",
                    marginTop: 5,
                  }}
                >
                  <ShowBannerContent
                    bannerContent={banner}
                    dropdownValue={banner.bannerType}
                  />
                </Box>
              )}
            </Box>
          </Grid>

          {viewMode ? null : (
            <Grid item xs={7}>
              <Box>
                {dropdownValue === "VIDEO" && (
                  <VideoList onSelect={selectDesination} />
                )}
              </Box>
              <Box>
                {dropdownValue === "PLAYLIST" && (
                  <PlayList onSelect={selectDesination} />
                )}
              </Box>
              <Box>
                {dropdownValue === "SUBSCRIPTION" && (
                  <SubscriptionList onSelect={selectDesination} />
                )}
              </Box>
              {dropdownValue !== "PLAYLIST" &&
                dropdownValue !== "SUBSCRIPTION" &&
                dropdownValue !== "VIDEO" && (
                  <Box textAlign="center" marginTop={40}>
                    <b style={{ color: "gray" }}>
                      Please Select Type Of the Banner To Upload Content
                    </b>
                  </Box>
                )}
            </Grid>
          )}
        </Grid>
        {/* <Box sx={{ marginTop: 2, padding: 2, textAlign: "right" }}>
          <Button variant="contained" onClick={handleSaveBanner}>
            Save Banner
          </Button>
        </Box> */}
      </Box>
    </Drawer>
  );
};

export default DrawerBanerManagement;

export const VideoList = ({
  onSelect,
}: {
  onSelect: (id: string) => Promise<number>;
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(false);
  const [totalElements, setTotalElements] = useState<number>(0);

  const rowsPerPageOptions = [5, 15, 25];
  const [rowsPerPage, setRowsPerPage] = useState<number>(rowsPerPageOptions[0]);

  const [page, setPage] = useState<number>(0);
  const [videos, setVideos] = useState<Video[]>([]);

  useEffect(() => {
    getVideos();
  }, [page, rowsPerPage]);

  const getVideos = async () => {
    setLoading(true);
    try {
      const response = await getVideoList({ page, size: rowsPerPage });
      setVideos(response.data.content);
      setTotalElements(response.data.totalElements);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  return (
    <Box
      sx={{
        display: "flex",
        backgroundColor: theme.palette.background.paper,
        flexDirection: "column",
        height: "100vh",
        minHeight: "100vh",
        maxHeight: "100vh",
        padding: theme.spacing(1),
        boxSizing: "border-box",
      }}
    >
      <Box
        mb={2}
        mt={2}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <TextField
          placeholder="Search by name"
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          marginBottom: theme.spacing(1),
          scrollbarWidth: "none",
        }}
      >
        <TableBodyVideoList
          drawer
          loading={loading}
          videos={videos}
          callBackchange={onSelect}
          //   disableChange={selectedPlayList}
          //   refreshList={onDataUpdate}
          //   storedVidIds={storedVidIds}
        />
      </Box>
      <Box sx={{ marginTop: "auto" }}>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    </Box>
  );
};

const PlayList = ({
  onSelect,
}: {
  onSelect: (id: string) => Promise<number>;
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(false);
  const [totalElements, setTotalElements] = useState<number>(0);
  const rowsPerPageOptions = [5, 15, 25];
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState<number>(rowsPerPageOptions[0]);
  const [page, setPage] = useState<number>(0);

  useEffect(() => {
    getPlayListContent();
  }, [page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const getPlayListContent = async () => {
    setLoading(true);
    try {
      const response = await getPlayList({ page, size: rowsPerPage });
      setPlaylists(response.data.content);
      setTotalElements(response.data.totalElements);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  return (
    <Box
      sx={{
        display: "flex",
        backgroundColor: theme.palette.background.paper,
        flexDirection: "column",
        height: "100vh",
        minHeight: "100vh",
        maxHeight: "100vh",
        padding: theme.spacing(1),
        boxSizing: "border-box",
      }}
    >
      {/* conflicts */}
      <Box
        mb={2}
        mt={2}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <TextField
          placeholder="Search by name"
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          marginBottom: theme.spacing(1),
          scrollbarWidth: "none",
        }}
      >
        <TableBodyPlayList
          drawer
          loading={loading}
          playlists={playlists}
          callBackchange={onSelect}
        />
      </Box>
      <Box sx={{ marginTop: "auto" }}>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    </Box>
  );
};

const SubscriptionList = ({
  onSelect,
}: {
  onSelect: (id: string) => Promise<number>;
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(false);
  const [totalElements, setTotalElements] = useState<number>(0);
  const rowsPerPageOptions = [5, 15, 25];
  const [subscriptions, setSubscriptions] = useState<ISubscription[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState<number>(rowsPerPageOptions[0]);
  const [page, setPage] = useState<number>(0);

  useEffect(() => {
    fetchSubscriptions();
  }, [page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const data = await GetSubScriptionList({ page, size: 10 });
      setSubscriptions(data.content);
      setTotalElements(data.totalElements);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box
      sx={{
        display: "flex",
        backgroundColor: theme.palette.background.paper,
        flexDirection: "column",
        height: "100vh",
        minHeight: "100vh",
        maxHeight: "100vh",
        padding: theme.spacing(1),
        boxSizing: "border-box",
      }}
    >
      <Box
        mb={2}
        mt={2}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <TextField
          placeholder="Search by name"
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          marginBottom: theme.spacing(1),
          scrollbarWidth: "none",
        }}
      >
        <TableBodySubsciptionList
          drawer
          loading={loading}
          Subscriptions={subscriptions}
          callBackchange={onSelect}
        />
      </Box>
      <Box sx={{ marginTop: "auto" }}>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    </Box>
  );
};

const ShowBannerContent = ({
  dropdownValue,
  bannerContent,
}: {
  dropdownValue: "VIDEO" | "PLAYLIST" | "SUBSCRIPTION";
  bannerContent: AppBanner | null;
}) => {
  const [selectedVideoContent, setSeletedVideoContent] = useState<Video>();
  const [selectedPlaylistContent, setSeletedPlaylistContent] =
    useState<Playlist>();
  const [selectedSubscriptionContent, setSelectedSubscriptionContent] =
    useState<ISubscription>();

  useEffect(() => {
    fetchBannerContent();
  }, [bannerContent]);

  const fetchBannerContent = async () => {
    if (bannerContent?.bannerType === "VIDEO") {
      const selectedVideo = bannerContent.destination as string;
      const BannerVideo = await getSingleVideo(selectedVideo);

      setSeletedVideoContent(BannerVideo);
    }

    if (bannerContent?.bannerType === "PLAYLIST") {
      const selectedPlayList = bannerContent.destination as string;
      const BannerPlaylist = await GetSinglePlayList({ selectedPlayList });
      setSeletedPlaylistContent(BannerPlaylist);
    }

    if (bannerContent?.bannerType === "SUBSCRIPTION") {
      const id = bannerContent.destination as string;

      const BannerSubscription = await GetSubscriptionById({ id });

      setSelectedSubscriptionContent(BannerSubscription);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        aspectRatio: "16 / 9",
      }}
    >
      {dropdownValue === "PLAYLIST" && selectedPlaylistContent && (
        <>
          <PlaylistThumbCard
            srcUrl={selectedPlaylistContent?.thumbnailUrl as string}
            count={selectedPlaylistContent?.videos.length as number}
            width={"100%"}
          />

          <Box display={"flex"} marginTop={2}>
            <Box
              component="img"
              src={selectedPlaylistContent.thumbnailUrl}
              sx={{
                width: 40,
                height: 40,
                objectFit: "cover",
                borderRadius: "50%",
                marginRight: 2,
              }}
            />
            <Typography variant="body2" color={"gray"}>
              <b>{selectedPlaylistContent.name.slice(0, 50)}</b> <br />
              {/* {freebie.playList.description.slice(0, 50)} */}
              <b>{selectedPlaylistContent.videos.length}</b> videos
            </Typography>
          </Box>
        </>
      )}
      {dropdownValue === "VIDEO" && selectedVideoContent && (
        <>
          <VideoThumbCard
            srcUrl={selectedVideoContent?.thumbnailUrl as string}
            duration={selectedVideoContent?.duration as number}
            width={"100%"}
          />
          <Box display={"flex"} marginTop={2}>
            <Box
              component="img"
              src={selectedVideoContent.thumbnailUrl}
              sx={{
                width: 40,
                height: 40,
                objectFit: "cover",
                borderRadius: "50%",
                marginRight: 2,
              }}
            />
            <Typography variant="body2" color={"gray"}>
              <b>{selectedVideoContent.title.slice(0, 50)}</b> <br />
              {/* {freebie.playList.description.slice(0, 50)} */}
              <b>{selectedVideoContent.totalLikes}</b> Likes &{" "}
              <b>{selectedVideoContent.totalComments}</b> Comments
            </Typography>
          </Box>
        </>
      )}
      {dropdownValue === "SUBSCRIPTION" && selectedSubscriptionContent && (
        <>
          <SubscriptionThumbCard
            srcUrl={selectedSubscriptionContent?.thumbnailUrl as string}
            width={"100%"}
          />

          <Box display={"flex"} marginTop={2}>
            <Box
              component="img"
              src={selectedSubscriptionContent.thumbnailUrl as string}
              sx={{
                width: 40,
                height: 40,
                objectFit: "cover",
                borderRadius: "50%",
                marginRight: 2,
              }}
            />
            <Typography variant="body2" color={"gray"}>
              <b>{selectedSubscriptionContent.name.slice(0, 50)}</b> <br />
              {/* {freebie.playList.description.slice(0, 50)} */}
              <b>{selectedSubscriptionContent.duration}</b> Days
            </Typography>
          </Box>
        </>
      )}

      {/* Add similar display logic for VIDEO and SUBSCRIPTION if needed */}
    </Box>
  );
};
